import { useEffect, useState } from 'react';
import { IAbookTab } from '@models/Forms/IForms';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { hasAbookTabVisibilityRules, keepSameAbookTabs } from '@utils/abookTabVisibility';

/**
 * Вкладки окна выбора из адресной книги с учётом условий показа (`visibilityRules`). Без условий — те же вкладки
 * сразу. С условиями до первого вычисления — `undefined`: окно показывает скелет списка, а не вкладку, которая
 * тут же исчезнет. Пересчёт — при смене вкладок и `revision` (значений, от которых зависят условия); пока он идёт,
 * остаётся прежний набор.
 */
export function useResolvedAbookTabs(
    tabs: IAbookTab[] | undefined,
    resolve: (tabs: IAbookTab[]) => Promise<IAbookTab[]>,
    revision?: unknown,
): IAbookTab[] | undefined {
    const withRules = hasAbookTabVisibilityRules(tabs);
    const resolveRef = useLatest(resolve);
    const [state, setState] = useState<{ source: IAbookTab[]; visible: IAbookTab[] }>();

    useEffect(() => {
        if (!tabs || !hasAbookTabVisibilityRules(tabs)) return;
        let canceled = false;
        void resolveRef
            .current(tabs)
            .catch((error: unknown) => {
                console.error(error);
                return tabs;
            })
            .then((visible) => {
                if (canceled) return;
                setState((prev) => {
                    const next = keepSameAbookTabs(prev?.source === tabs ? prev.visible : undefined, visible);
                    return prev?.source === tabs && prev.visible === next ? prev : { source: tabs, visible: next };
                });
            });
        return () => {
            canceled = true;
        };
    }, [tabs, revision, resolveRef]);

    if (!withRules) return tabs;
    return state?.source === tabs ? state.visible : undefined;
}
